import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  IAcademicsSchedule,
  EScheduleType,
  ISchedulePeriod,
} from '../../../../../apis/academics/academicsSchedules.interfaces';
import { Chip, Stack, Tooltip } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { TTranslateTextType } from 'src/hooks/useTranslationHook';

export const getSchedulesGridColumns = (
  translateText: TTranslateTextType
): GridColDef[] => {
  return [
    {
      field: 'name',
      headerName: translateText('academics.schedules.columns.scheduleName'),
      width: 180,
      flex: 1,
    },
    {
      field: 'type',
      headerName: translateText('academics.schedules.columns.type'),
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const schedule = params.row as IAcademicsSchedule;
        const chipColor = getScheduleTypeColor(schedule.type);

        return (
          <Chip
            label={schedule.type}
            size="small"
            color={chipColor}
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'academicYear',
      headerName: translateText('academics.schedules.columns.academicYear'),
      width: 150,
    },
    {
      field: 'term',
      headerName: translateText('academics.schedules.columns.term'),
      width: 130,
      renderCell: (params: GridRenderCellParams) => {
        const schedule = params.row as IAcademicsSchedule;
        return <span>{schedule.term || '-'}</span>;
      },
    },
    {
      field: 'periods',
      headerName: translateText('academics.schedules.columns.periods'),
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        const schedule = params.row as IAcademicsSchedule;
        return <span>{schedule.periods.length}</span>;
      },
    },
    {
      field: 'timeRange',
      headerName: translateText('academics.schedules.columns.timeRange'),
      width: 180,
      renderCell: (params: GridRenderCellParams) => {
        const schedule = params.row as IAcademicsSchedule;
        const timeRange = getScheduleTimeRange(schedule.periods);

        return (
          <Tooltip
            title={translateText('academics.schedules.scheduleTimeRange')}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <AccessTimeIcon fontSize="small" color="action" />
              <span>{timeRange}</span>
            </Stack>
          </Tooltip>
        );
      },
    },
    {
      field: 'isActive',
      headerName: translateText('academics.schedules.columns.status'),
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        const schedule = params.row as IAcademicsSchedule;
        return (
          <Chip
            label={
              schedule.isActive
                ? translateText('academics.schedules.active')
                : translateText('academics.schedules.inactive')
            }
            color={schedule.isActive ? 'success' : 'default'}
            size="small"
          />
        );
      },
    },
    {
      field: 'createdAt',
      headerName: translateText('academics.schedules.columns.created'),
      width: 170,
      renderCell: (params: GridRenderCellParams) => {
        const schedule = params.row as IAcademicsSchedule;
        return <span>{formatDate(schedule.createdAt)}</span>;
      },
    },
    {
      field: 'updatedAt',
      headerName: translateText('academics.schedules.columns.lastUpdated'),
      width: 170,
      renderCell: (params: GridRenderCellParams) => {
        const schedule = params.row as IAcademicsSchedule;
        return <span>{formatDate(schedule.updatedAt)}</span>;
      },
    },
  ];
};

export const getSchedulePeriodsGridColumns = (
  translateText: TTranslateTextType
): GridColDef[] => {
  return [
    {
      field: 'periodNumber',
      headerName: translateText('academics.schedules.columns.period'),
      width: 100,
    },
    {
      field: 'timeSlot',
      headerName: translateText('academics.schedules.columns.time'),
      width: 160,
      renderCell: (params: GridRenderCellParams) => {
        const period = params.row as ISchedulePeriod;
        return <span>{`${period.startTime} - ${period.endTime}`}</span>;
      },
    },
    {
      field: 'dayOfWeek',
      headerName: translateText('academics.schedules.columns.day'),
      width: 120,
    },
    {
      field: 'courseId',
      headerName: translateText('academics.schedules.columns.course'),
      width: 180,
      renderCell: (params: GridRenderCellParams) => {
        const period = params.row as ISchedulePeriod;
        // This would typically be replaced with a lookup of the course name
        return <span>{period.courseId || '-'}</span>;
      },
    },
    {
      field: 'teacherId',
      headerName: translateText('academics.schedules.columns.teacher'),
      width: 180,
      renderCell: (params: GridRenderCellParams) => {
        const period = params.row as ISchedulePeriod;
        // This would typically be replaced with a lookup of the teacher name
        return <span>{period.teacherId || '-'}</span>;
      },
    },
    {
      field: 'classId',
      headerName: translateText('academics.schedules.columns.class'),
      width: 180,
      renderCell: (params: GridRenderCellParams) => {
        const period = params.row as ISchedulePeriod;
        // This would typically be replaced with a lookup of the class name
        return <span>{period.classId || '-'}</span>;
      },
    },
    {
      field: 'room',
      headerName: translateText('academics.schedules.columns.room'),
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        const period = params.row as ISchedulePeriod;
        return <span>{period.room || '-'}</span>;
      },
    },
    {
      field: 'isBreak',
      headerName: translateText('academics.schedules.columns.break'),
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        const period = params.row as ISchedulePeriod;
        if (!period.isBreak) return <span>No</span>;

        return <Chip label={period.breakType} color="secondary" size="small" />;
      },
    },
  ];
};

// Helper functions
const getScheduleTypeColor = (
  type: EScheduleType
):
  | 'primary'
  | 'secondary'
  | 'error'
  | 'info'
  | 'success'
  | 'warning'
  | 'default' => {
  switch (type) {
    case EScheduleType.REGULAR:
      return 'primary';
    case EScheduleType.EXAM:
      return 'error';
    case EScheduleType.SPECIAL_EVENT:
      return 'secondary';
    case EScheduleType.HOLIDAY:
      return 'success';
    default:
      return 'default';
  }
};

const getScheduleTimeRange = (periods: ISchedulePeriod[]): string => {
  if (!periods || periods.length === 0) return 'N/A';

  let earliestTime = '23:59';
  let latestTime = '00:00';

  periods.forEach((period) => {
    if (period.startTime < earliestTime) earliestTime = period.startTime;
    if (period.endTime > latestTime) latestTime = period.endTime;
  });

  return `${earliestTime} - ${latestTime}`;
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};
